import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgModule, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingAttributeComponent, ListLoadingIndicatorComponent } from '@shared/ui';
import { TranslateModule } from '@ngx-translate/core';
import { LabelLanguages, SaveLabelLanguage } from '@telespot/domain';

export interface LabelListItem {
  uuid: string;
  languages: LabelLanguages;
}

const DEFAULT_LANGUAGES: SaveLabelLanguage[] = ['en', 'es', 'pt', 'fr', 'it'];

/**
 * Enum for the columns of the labels list.
 * Languages are undefined because the columns are dynamically generated based on the user's current language.
 * This way we show the user's language first.
 */
export enum LabelColumn {
  LANG_1 = 'lang1',
  LANG_2 = 'lang2',
  LANG_3 = 'lang3',
  LANG_4 = 'lang4',
  LANG_5 = 'lang5',
  UUID = 'uuid',
  ACTIONS = 'actions',
}

/**
 * The language columns the template actually defines, in positional order. The
 * ordering rule is capped against this list, so the table can never be asked for
 * a language column that does not exist.
 */
const LANGUAGE_COLUMNS = [
  LabelColumn.LANG_1,
  LabelColumn.LANG_2,
  LabelColumn.LANG_3,
  LabelColumn.LANG_4,
  LabelColumn.LANG_5,
] as const;

@Component({
    selector: 'ts-labels-list',
    templateUrl: './labels-list.component.html',
    styleUrls: ['./labels-list.component.scss'],
    standalone: false
})
export class LabelsListComponent implements OnChanges {
  @Input() items: LabelListItem[] = [];
  @Input() loading = false;
  @Input() noLabels = false;
  @Input() currentLanguage = 'en';
  /**
   * Blocks the colour action while the page persists a previous pick — a second
   * dialog would open on an undrained ledger and re-save the same pairs.
   */
  @Input() colorActionDisabled = false;

  @Output() editClicked = new EventEmitter<LabelListItem>();
  /** Palette action: open the colour editor for this row's label. */
  @Output() colorClicked = new EventEmitter<LabelListItem>();

  public readonly LabelColumn = LabelColumn;

  // Store the container height when loading starts to prevent layout jumps
  public containerHeight: number | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When loading starts, capture the current height
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    // When loading ends, clear the fixed height
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }

  /**
   * Get the ordered language columns based on user's current language
   * User's language comes first, followed by the other default languages
   */
  public get displayedColumns(): string[] {
    return [...LANGUAGE_COLUMNS, LabelColumn.UUID, LabelColumn.ACTIONS];
  }

  /**
   * Get the ordered list of language codes based on user preference.
   * Always returns exactly the default languages — only ever a reordering of them —
   * so every language column the template defines has a language to render.
   */
  public getOrderedLanguages(): SaveLabelLanguage[] {
    const preferred = this.resolveLabelLanguage(this.currentLanguage);

    // A language the labels have no column for keeps the default ordering:
    // a column keyed on it could never hold a value.
    return preferred
      ? [preferred, ...DEFAULT_LANGUAGES.filter((lang) => lang !== preferred)]
      : [...DEFAULT_LANGUAGES];
  }

  /**
   * Resolve an interface language to one of the label languages, or null when the
   * labels do not carry it. The interface language comes from the translation
   * service and may be seeded from a stored browser preference, so it is untrusted:
   * it can be a regional tag such as `en-US`, or a language the labels never had.
   */
  private resolveLabelLanguage(language: string): SaveLabelLanguage | null {
    const base = (language ?? '').toLowerCase().split(/[-_]/)[0] as SaveLabelLanguage;
    return DEFAULT_LANGUAGES.includes(base) ? base : null;
  }

  /**
   * Get the language code for a specific column position
   */
  public getLanguageForColumn(columnIndex: number): SaveLabelLanguage {
    const orderedLangs = this.getOrderedLanguages();
    return orderedLangs[columnIndex] || 'en';
  }

  /**
   * Extract label value for a specific language
   */
  public getLabelValue(languages: LabelLanguages, lang: SaveLabelLanguage): string {
    return languages[lang] ?? '';
  }

  /**
   * Handle row click to emit edit event
   */
  public onRowClick(item: LabelListItem): void {
    this.editClicked.emit(item);
  }
}

@NgModule({
  declarations: [LabelsListComponent],
  exports: [LabelsListComponent],
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LoadingAttributeComponent,
    ListLoadingIndicatorComponent,
  ],
})
export class LabelsListComponentModule {}
